import { Component } from "@theme/component";
import { trapFocus, removeTrapFocus } from "@theme/focus";
import { onAnimationEnd } from "@theme/utilities";

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
    requiredRefs = ["details"];

    connectedCallback() {
        super.connectedCallback();

        this.addEventListener("keyup", this.#onKeyUp);
        this.#setupAnimatedElementListeners();

        // Mobile navigation interactions
        this.#wireMobileNav();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener("keyup", this.#onKeyUp);
    }

    /**
     * Close the main menu drawer when the Escape key is pressed
     * @param {KeyboardEvent} event
     */
    #onKeyUp = (event) => {
        if (event.key !== "Escape") return;

        this.#close(this.#getDetailsElement(event));
    };

    /**
     * @returns {boolean} Whether the main menu drawer is open
     */
    get isOpen() {
        return this.refs.details.hasAttribute("open");
    }

    /**
     * Get the closest details element to the event target
     * @param {Event | undefined} event
     * @returns {HTMLDetailsElement}
     */
    #getDetailsElement(event) {
        if (!(event?.target instanceof Element)) return this.refs.details;

        return event.target.closest("details") ?? this.refs.details;
    }

    /**
     * Toggle the main menu drawer
     */
    toggle() {
        return this.isOpen ? this.close() : this.open();
    }

    /**
     * Open the closest drawer or the main menu drawer
     * @param {Event} [event]
     */
    open(event) {
        const details = this.#getDetailsElement(event);
        const summary = details.querySelector("summary");

        if (!summary) return;

        summary.setAttribute("aria-expanded", "true");
        requestAnimationFrame(() => {
            details.classList.add("menu-open");
            setTimeout(() => {
                trapFocus(details);
            }, 0);
        });
    }

    /**
     * Go back or close the main menu drawer
     * @param {Event} [event]
     */
    back(event) {
        this.#close(this.#getDetailsElement(event));
    }

    /**
     * Close the main menu drawer
     */
    close() {
        this.#close(this.refs.details);
    }

    /**
     * Close the closest menu or submenu that is open
     *
     * @param {HTMLDetailsElement} details
     */
    #close(details) {
        const summary = details.querySelector("summary");

        if (!summary) return;

        summary.setAttribute("aria-expanded", "false");
        details.classList.remove("menu-open");

        onAnimationEnd(details, () => {
            reset(details);

            if (details === this.refs.details) {
                removeTrapFocus();
                const openDetails = this.querySelectorAll("details[open]");
                openDetails.forEach(reset);
            } else {
                setTimeout(() => {
                    trapFocus(this.refs.details);
                }, 0);
            }
        });
    }

    /**
     * Attach animationend event listeners to all animated elements to remove will-change after animation
     * to remove the stacking context and allow submenus to be positioned correctly
     */
    #setupAnimatedElementListeners() {
        /**
         * @param {AnimationEvent} event
         */
        function removeWillChangeOnAnimationEnd(event) {
            const target = event.target;
            if (target && target instanceof HTMLElement) {
                target.style.setProperty("will-change", "unset");
                target.removeEventListener("animationend", removeWillChangeOnAnimationEnd);
            }
        }
        const allAnimated = this.querySelectorAll(".menu-drawer__animated-element");
        allAnimated.forEach((element) => {
            element.addEventListener("animationend", removeWillChangeOnAnimationEnd);
        });
    }

    /**
     * Wire up mobile navigation brand list -> collection panel transitions
     */
    #wireMobileNav() {
        const root = this;
        const brandsList = this.querySelector(".mobile-nav__brand-list");
        if (brandsList) {
            brandsList.addEventListener("click", (e) => {
                const btn = /** @type {HTMLElement} */ (e.target.closest(".mobile-nav__brand-link"));
                if (!btn) return;
                const handle = btn.getAttribute("data-collection-handle");
                if (!handle) return;
                this.#openCollectionPanel(handle);
            });
        }

        // Back buttons inside collection panels
        this.querySelectorAll(".mobile-nav__panel--collection .mobile-nav__back").forEach((b) => {
            b.addEventListener("click", () => this.#showBrandsPanel());
        });
    }

    /**
     * Show the brands list panel
     */
    #showBrandsPanel() {
        this.querySelectorAll(".mobile-nav__panel--collection").forEach((p) => p.setAttribute("hidden", ""));
        const brandsHeader = this.querySelector(".mobile-nav__brand");
        if (brandsHeader) {
            const name = this.querySelector(".menu-drawer")?.getAttribute("data-shop-name") || "";
            brandsHeader.textContent = name;
        }
        const body = this.querySelector(".mobile-nav__brand-list");
        if (body) body.removeAttribute("hidden");

        // Show topbar and step-1 section header again
        this.querySelector(".menu-drawer__header.mobile-nav__topbar")?.removeAttribute("hidden");
        this.querySelector(".mobile-nav__section-header")?.removeAttribute("hidden");

        // Reset step state class on drawer root
        this.querySelector(".menu-drawer")?.classList.remove("is-step-2");
        this.querySelector(".menu-drawer")?.classList.add("is-step-1");
    }

    /**
     * Open a specific collection panel by handle
     * @param {string} handle
     */
    #openCollectionPanel(handle) {
        // Hide brands list
        const list = this.querySelector(".mobile-nav__brand-list");
        list?.setAttribute("hidden", "");

        // Update top-left header to back + Brands label
        const brandHeader = this.querySelector(".mobile-nav__brand");
        if (brandHeader) {
            brandHeader.innerHTML = `
        <button type="button" class="mobile-nav__back" aria-label="Back">
          <svg class="mobile-nav__back-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M246.6 297.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L314.7 320 484 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5 0-45.3l-192 192z"/></svg>
          <span>Brands</span>
        </button>`;
            brandHeader.querySelector(".mobile-nav__back")?.addEventListener("click", () => this.#showBrandsPanel());
        }

        // Hide topbar and the step-1 section header while viewing step-2 panel
        this.querySelector(".menu-drawer__header.mobile-nav__topbar")?.setAttribute("hidden", "");
        this.querySelector(".mobile-nav__section-header")?.setAttribute("hidden", "");

        // Mark drawer as step 2 for CSS-driven visibility
        this.querySelector(".menu-drawer")?.classList.add("is-step-2");
        this.querySelector(".menu-drawer")?.classList.remove("is-step-1");

        // Show chosen panel
        this.querySelectorAll(".mobile-nav__panel--collection").forEach((p) => {
            if (p.getAttribute("data-collection") === handle) {
                p.removeAttribute("hidden");
            } else {
                p.setAttribute("hidden", "");
            }
        });
    }
}

if (!customElements.get("header-drawer")) {
    customElements.define("header-drawer", HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
    element.classList.remove("menu-open");
    element.removeAttribute("open");
    element.querySelector("summary")?.setAttribute("aria-expanded", "false");
}
